import * as React from 'react';

import type { W_Account_FindAllForReportTypeView_VM_User } from 'ab-api/web/account/findAllForReportTypeView/viewModel';
import type { W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single } from 'ab-api/web/reportType/findAllReportBlockFieldsForFilter';

import type { PreviewVM as AccountVM } from 'ab-viewModels/account/preview.viewModel';

import { ParsedCompoundUnit } from '@acceligentllc/shared/utils/unit';

import { CompoundUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import { Type as ReportBlockFieldType } from '@acceligentllc/shared/enums/reportBlockField';

import ColorSquare from 'af-components/ColorSquare';
import DropdownWithSecondaryPicker from 'af-components/Controls/DropdownWithSecondaryPicker';
import DropdownSecondaryMultiple from 'af-components/Controls/DropdownWithSecondaryPicker/DropdownSecondaryMultiple';
import type { DropdownProps } from 'af-components/Controls/DropdownWithSecondaryPicker/TextSecondaryMultiple';
import TextSecondaryMultiple from 'af-components/Controls/DropdownWithSecondaryPicker/TextSecondaryMultiple';

import BooleanFieldFilter from './BooleanFieldFilter';
import type { RangeProps } from './NumericFieldFilter';
import NumericFieldFilter from './NumericFieldFilter';
import styles from './styles.module.scss';
import { isSecondaryNumeric, type SecondaryBoolean, type SecondaryEquipment, type SecondaryFilter, type SecondaryNumeric, type SecondarySignature, type SecondaryTextual } from './types';
import { ReportTypeViewTableAdditionalFilterType, type ReportTypeViewTableAdditionalFieldFilter, type ReportTypeViewTableAdditionalFilter, ComparatorType, isOtherFilterType, isTextualType, ComparatorLabelMap, isBooleanType, isNumericType } from '../../types';
import type { ReportTypeViewFieldFilter } from '../../types';

type Props = {
	reportBlockFieldOptions: W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single[];
	equipmentOptions: {
		id: number;
		code: string;
		specification: Nullable<string>;
	}[];
	lazyLoadEquipmentOptions: (isLoaded: boolean) => Promise<void>;
	accountOptions: W_Account_FindAllForReportTypeView_VM_User[];
	lazyLoadAccountOptions: (isLoaded: boolean) => Promise<void>;
	lazyLoadReportBlockFieldOptions: (isLoaded: boolean) => Promise<void>;
	filter: ReportTypeViewTableAdditionalFieldFilter;
	editFilter: (id: string | number, newValue: ReportTypeViewTableAdditionalFilter) => void;
	removeFilter: (id: string | number) => void;
	initiallyOpened: boolean;
};

const compoundToString = (value: Record<string, Nullable<number>>) => {
	return Object.keys(value)
		.toReversed() // the data is in reversed order, first major units then minor
		.map((_singularCompound) => `${value[_singularCompound] ?? 0} ${_singularCompound}`)
		.join(' ');
};

const renderMultipleSelected = (reportBlockFieldName: string, selected: Nullable<string>[]) => {
	const showCount = selected.length > 1;

	return (
		<div className={styles['selected-multiple']}>
			{`${reportBlockFieldName}: `}
			{`${selected[0] ?? 'EMPTY'}`}
			{showCount && (
				<span className={styles['selected-field-count']}>
					{`+ ${selected.length - 1}`}
				</span>
			)}
		</div>
	);
};

const isFilterValueIncomplete = (value: ReportTypeViewFieldFilter['value'], type?: ComparatorType) => {
	if (Array.isArray(value) && !value.length) {
		return true;
	}

	if (type !== ComparatorType.EMPTY && (value === null || value === undefined)) {
		return true;
	}

	if (
		typeof value === 'object' &&
		value !== null &&
		'min' in value &&
		'max' in value
	) {
		const { min, max } = value;

		// If either bound missing, treat as invalid
		if (min === null || max === null) {
			return true;
		}

		if (max < min) {
			return true;
		}
	}

	return false;
};

const renderEquipmentOptionItem = (option) => {
	return (
		<div className={styles['equipment-field']} key={option.equipmentId}>
			{option.color && <ColorSquare color={option.color} />}
			{option.code}
			{option.specification &&
				<span>
					{option.specification}
				</span>
			}
		</div>
	);
};

const renderAccountOptionItem = (_account: AccountVM) => <>{`${_account.fullName} (${_account.uniqueId})`}</>;

const _defaultSecondaryOption = (_filterValue: Nullable<ReportTypeViewTableAdditionalFieldFilter['value']>): Nullable<SecondaryFilter> => {
	if (!_filterValue) {
		return null;
	}

	return {
		type: _filterValue.type,
		value: _filterValue.value,
		comparator: _filterValue.comparator,
	} as SecondaryFilter;
};

const FieldFilter: React.FC<Props> = (props) => {
	const {
		editFilter,
		filter,
		removeFilter,
		reportBlockFieldOptions,
		lazyLoadEquipmentOptions,
		equipmentOptions,
		accountOptions,
		lazyLoadAccountOptions,
		lazyLoadReportBlockFieldOptions,
		initiallyOpened,
	} = props;

	const findReportBlockOptionForId = React.useCallback((reportBlockFieldOptionId: Nullable<string>) => {
		return reportBlockFieldOptions.find((_option) => _option.id === reportBlockFieldOptionId) ?? null;
	}, [reportBlockFieldOptions]);

	const [selectedPrimary, setSelectedPrimary] = React.useState<Nullable<W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single>>(null);

	const [selectedSecondary, setSelectedSecondary] = React.useState<Nullable<SecondaryFilter>>(_defaultSecondaryOption(filter?.value ?? null));

	// finding it in initial value of selectedPrimary isn't sufficient since reportBlockFieldOptions are lazy loaded
	const selected = React.useMemo(() => {
		if (!selectedPrimary) {
			return reportBlockFieldOptions.find((_option) => _option.id === filter.value?.reportBlockFieldOptionId) ?? null;
		}
		return reportBlockFieldOptions.find((_option) => _option.id === selectedPrimary.id) ?? null;
	}, [filter.value?.reportBlockFieldOptionId, reportBlockFieldOptions, selectedPrimary]);

	const setBooleanFilterValue = React.useCallback((newValue: boolean) => {
		setSelectedSecondary({
			...selectedSecondary as SecondaryBoolean,
			value: newValue,
		});
	}, [selectedSecondary]);

	const setEquipmentFilterValue = React.useCallback((newValues: SecondaryEquipment['value']) => {
		setSelectedSecondary({
			...selectedSecondary as SecondaryEquipment,
			value: newValues,
		});

	}, [selectedSecondary]);

	const setSignatureFilterValue = React.useCallback((newValues: SecondarySignature['value']) => {
		setSelectedSecondary({
			...selectedSecondary as SecondarySignature,
			value: newValues,
		});

	}, [selectedSecondary]);

	const setSecondaryOption = React.useCallback((values: string[]) => {
		setSelectedSecondary({
			...selectedSecondary as SecondaryTextual,
			value: values,
		});
	}, [selectedSecondary]);

	const setSecondaryNumericValue = React.useCallback((value: SecondaryNumeric['value']) => {
		setSelectedSecondary({
			...selectedSecondary,
			value,
		} as SecondaryNumeric);
	}, [selectedSecondary]);

	const setSecondaryNumericOptionComparator = React.useCallback((comparator: ComparatorType) => {
		if (comparator === ComparatorType.EMPTY) {

			setSelectedSecondary({
				type: selectedSecondary?.type,
				comparator: ComparatorType.EMPTY,
				value: null,
			} as SecondaryNumeric);
		} else if (comparator === ComparatorType.RANGE) {
			setSelectedSecondary({
				...selectedSecondary,
				comparator,
				value: {
					min: null,
					max: null,
				},
			} as SecondaryNumeric);
		} else {
			setSelectedSecondary({
				...selectedSecondary,
				value: selectedSecondary?.comparator === ComparatorType.RANGE
					? null
					: selectedSecondary?.value ?? null,
				comparator,
			} as SecondaryNumeric);
		}
	}, [selectedSecondary]);

	const renderSecondaryFilter = React.useCallback((selectedPrimaryFilter: W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single) => {
		if (!selectedPrimaryFilter) {
			return null;
		}
		if (selectedPrimaryFilter.type === ReportBlockFieldType.IMAGE) {
			return (
				<TextSecondaryMultiple
					hasEmptyOption={true}
					hasFreeInputOption={false}
					initiallyOpened={true}
					options={[]}
					placeholder="" // intentional
					secondaryOptions={(selectedSecondary as SecondaryTextual).value}
					setSecondaryOption={setSecondaryOption}
					withDropdownOptions={true}
				/>
			);
		}

		if (isTextualType(selectedPrimaryFilter.type)) {
			let dropdownProps: DropdownProps = { withDropdownOptions: false };
			let placeholder = 'Select Text';
			if (selectedPrimaryFilter.type === ReportBlockFieldType.DROPDOWN) {
				placeholder = selectedPrimaryFilter.allowCustomDropdownListValue
					? `Select or Enter ${selectedPrimaryFilter.reportBlockFieldName}`
					: `Select ${selectedPrimaryFilter.reportBlockFieldName}`;
				dropdownProps = {
					withDropdownOptions: true,
					options: selectedPrimaryFilter.options ?? [],
					hasFreeInputOption: selectedPrimaryFilter.allowCustomDropdownListValue,
					initiallyOpened: true,
				};
			} else if (selectedPrimaryFilter.type === ReportBlockFieldType.TIME) {
				placeholder = 'Select HH:MM';
			}
			return (
				<TextSecondaryMultiple
					hasEmptyOption={true}
					key={selectedPrimaryFilter.id}
					placeholder={placeholder}
					secondaryOptions={(selectedSecondary as SecondaryTextual).value}
					setSecondaryOption={setSecondaryOption}
					{...dropdownProps}
				/>
			);
		} else if (selectedSecondary && isSecondaryNumeric(selectedSecondary)) {
			let rangeProps: RangeProps;
			if (selectedSecondary.comparator === ComparatorType.RANGE) {
				rangeProps = {
					isRange: true,
					value: selectedSecondary.value,
					setValue: setSecondaryNumericValue,
				};
			} else {
				rangeProps = {
					isRange: false,
					value: selectedSecondary.value,
					setValue: setSecondaryNumericValue,
				};
			}

			return (
				<NumericFieldFilter
					comparator={selectedSecondary?.comparator ?? null}
					name={selectedPrimaryFilter.reportBlockFieldName}
					setComparator={setSecondaryNumericOptionComparator}
					unit={selectedPrimaryFilter.unit}
					{...rangeProps}
				/>
			);
		} else if (isBooleanType(selectedPrimaryFilter.type)) {
			return (
				<BooleanFieldFilter
					selected={(selectedSecondary as SecondaryBoolean).value}
					setSelected={setBooleanFilterValue}
				/>
			);
		} else if (selectedPrimaryFilter.type === ReportBlockFieldType.EQUIPMENT) {
			return (
				<DropdownSecondaryMultiple
					filterable={true}
					filterBy={['code']}
					idKey="id"
					lazyLoad={lazyLoadEquipmentOptions}
					options={equipmentOptions}
					placeholder="Select Equipment"
					renderMenuItemLabel={renderEquipmentOptionItem}
					selected={(selectedSecondary as SecondaryEquipment).value}
					setSelected={setEquipmentFilterValue}
				/>
			);
		} else if (selectedPrimaryFilter.type === ReportBlockFieldType.SIGNATURE) {
			return (
				<DropdownSecondaryMultiple
					filterable={true}
					filterBy={['fullName']}
					idKey="id"
					lazyLoad={lazyLoadAccountOptions}
					options={accountOptions}
					placeholder="Select Signee"
					renderMenuItemLabel={renderAccountOptionItem}
					selected={(selectedSecondary as SecondarySignature).value}
					setSelected={setSignatureFilterValue}
				/>
			);
		} else {
			return null;
		}
	}, [accountOptions,
		equipmentOptions,
		lazyLoadAccountOptions,
		lazyLoadEquipmentOptions,
		selectedSecondary,
		setBooleanFilterValue,
		setEquipmentFilterValue,
		setSecondaryOption,
		setSignatureFilterValue,
		setSecondaryNumericOptionComparator,
		setSecondaryNumericValue,
	]);

	const onApplyFilterClick = React.useCallback((
		primaryOption: Nullable<W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single>,
		secondaryOption: Nullable<SecondaryFilter>
	) => {

		if (!primaryOption || !secondaryOption) {
			return;
		}

		if (isOtherFilterType(secondaryOption.type)) {
			return;
		}

		editFilter(filter.id, {
			id: filter.id,
			type: ReportTypeViewTableAdditionalFilterType.FIELD,
			value: {
				name: primaryOption.reportBlockFieldName,
				comparator: secondaryOption.comparator,
				type: secondaryOption.type,
				value: secondaryOption.value,
				reportBlockFieldOptionId: primaryOption.id,
				unit: primaryOption.unit ?? null,
			} as ReportTypeViewFieldFilter,
		});
	}, [editFilter, filter.id]);

	const onPrimaryOptionClick = React.useCallback((primaryOption: W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single) => {
		if (primaryOption.id === selected?.id) {
			return;
		}
		let newSecondary: Nullable<SecondaryFilter> = null;

		if (isTextualType(primaryOption.type)) {
			newSecondary = {
				type: primaryOption.type,
				comparator: ComparatorType.EQUAL,
				value: [],
			};
		} else if (isBooleanType(primaryOption.type)) {
			newSecondary = {
				type: primaryOption.type,
				comparator: ComparatorType.EQUAL,
				value: false,
			};
		} else if (isNumericType(primaryOption.type)) {
			newSecondary = {
				type: primaryOption.type,
				comparator: ComparatorType.EQUAL,
				value: null,
			};
		} else if (primaryOption.type === ReportBlockFieldType.EQUIPMENT) {
			newSecondary = {
				type: primaryOption.type,
				comparator: ComparatorType.EQUAL,
				value: [],
			};
		} else if (primaryOption.type === ReportBlockFieldType.SIGNATURE) {
			newSecondary = {
				type: primaryOption.type,
				comparator: ComparatorType.EQUAL,
				value: [],
			};
		}
		else {
			throw new Error('Unsupported field type');
		}

		setSelectedPrimary(findReportBlockOptionForId(primaryOption.id));

		setSelectedSecondary(newSecondary);
	}, [findReportBlockOptionForId, selected?.id]);

	const removeCurrentFilter = React.useCallback(() => removeFilter(filter.id), [filter.id, removeFilter]);

	const renderPlaceholder = React.useCallback(() => {
		return (
			<div className={styles.placeholder}>
				<span className={styles.placeholder__label}>
					Field
				</span>
				<span className={styles.placeholder__icons}>
					<span className="icon-down" />
				</span>
			</div>
		);
	}, []);

	const renderSelected = React.useCallback(() => {
		if (!filter || isFilterValueIncomplete(filter.value?.value ?? null, filter.value?.comparator)) {
			return renderPlaceholder();
		}

		switch (filter.value?.type) {
			case ReportBlockFieldType.NUMERIC_ATTRIBUTE:
			case ReportBlockFieldType.CALCULATED: {
				const comparator = ComparatorLabelMap[filter.value.comparator];
				// compound range
				if (filter.value.comparator === ComparatorType.RANGE && filter.value.unit && CompoundUnitEnum[filter.value.unit]) {
					const resolvedMin = ParsedCompoundUnit[filter.value.unit](filter.value.value.min);
					const resolvedMax = ParsedCompoundUnit[filter.value.unit](filter.value.value.max);

					const minToCompound = compoundToString(resolvedMin);
					const maxToCompound = compoundToString(resolvedMax);

					return (
						<span className={styles.selected}>
							{`${filter.value.name}: ${minToCompound} - ${maxToCompound}`}
						</span>
					);
				}

				// non compound range
				if (filter.value.comparator === ComparatorType.RANGE) {
					const unitString = filter.value.unit ? ` ${filter.value.unit}` : '';
					return (
						<span className={styles.selected}>
							{`${filter.value.name}: ${filter.value.value.min}${unitString} - ${filter.value.value.max}${unitString}`};
						</span>
					);
				}

				// compound non range
				if (filter.value.unit && CompoundUnitEnum[filter.value.unit]) {
					const resolvedValue = ParsedCompoundUnit[filter.value.unit](filter.value.value);
					const valueToCompound = compoundToString(resolvedValue);

					return (
						<span className={styles.selected}>
							{`${filter.value.name}: ${valueToCompound}`}
						</span>
					);
				}

				if (filter.value.comparator === ComparatorType.EMPTY) {
					return (
						<span className={styles.selected}>
							{`${filter.value.name}: ${comparator.toLocaleUpperCase()}`}
						</span>
					);
				}

				return (
					<span className={styles.selected}>
						{`${filter.value.name}: ${comparator} ${filter.value.value ?? 0}`}
					</span>
				);
			}
			case ReportBlockFieldType.TEXTUAL_ATTRIBUTE:
			case ReportBlockFieldType.INFORMATION:
			case ReportBlockFieldType.DROPDOWN:
			case ReportBlockFieldType.IMAGE:
			case ReportBlockFieldType.TIME:
			case ReportBlockFieldType.ID:
			case ReportBlockFieldType.ADDRESS: {
				return (
					<span className={styles.selected}>
						{renderMultipleSelected(filter.value.name, filter.value.value)}
					</span>
				);
			}
			case ReportBlockFieldType.BOOLEAN:
			case ReportBlockFieldType.COMPLETION: {
				return (
					<span className={styles.selected}>
						{`${filter.value.name}: ${filter.value.value ? 'Yes' : 'No'}`}
					</span>);
			}
			case ReportBlockFieldType.EQUIPMENT: {
				const adjustedFieldValues = filter.value.value
					.map((_value) => {
						if (_value === null) {
							return null;
						}
						const _specification = _value.specification ? ` ${_value.specification}` : '';
						return `${_value.code}${_specification}`;
					});

				return (
					<span className={styles.selected}>
						{renderMultipleSelected(filter.value.name, adjustedFieldValues)}
					</span>
				);
			}
			case ReportBlockFieldType.SIGNATURE: {
				const adjustedFieldValues = filter.value.value.map((_signature) => _signature ? _signature.fullName : null);
				return (
					<span className={styles.selected}>
						{renderMultipleSelected(filter.value.name, adjustedFieldValues)}
					</span>
				);
			}
			case ReportBlockFieldType.IMMUTABLE_TEXT:
			case ReportBlockFieldType.LINE_BREAK:
			case ReportBlockFieldType.IMMUTABLE_IMAGE:
			default: {
				return null;
			}
		}
	}, [filter, renderPlaceholder]);

	const withCaret = !!filter.value?.value;

	const toggleClassName = React.useMemo(() => {
		if (isFilterValueIncomplete(filter.value?.value ?? null, filter.value?.comparator)) {
			return `${styles.toggle} ${styles['toggle--empty']}`;
		}
		return `${styles.toggle} ${styles['toggle--non-empty']}`;
	}, [filter.value?.comparator, filter.value?.value]);

	const renderPrimaryFilterItem = React.useCallback((_item: W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single) => {
		const mainBlockName = `${_item.mainBlockName ? _item.mainBlockName : ''}`;

		return (
			<div className={styles['report-block-field-item']}>
				{mainBlockName && (
					<span className={styles['report-block-field-item__block-name']}>{mainBlockName}</span>
				)}
				<span className={styles['report-block-field-item__block-name']}>{_item.reportBlockName}</span>
				<span className={styles['report-block-field-item__field-name']}>{_item.reportBlockFieldName}</span>
			</div>
		);
	}, []);

	const shouldDisableApplyButton = React.useCallback((
		primary: Nullable<W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single>,
		secondary: Nullable<SecondaryFilter>
	) => {
		if (!primary || !secondary) {
			return true;
		}

		if (secondary.comparator === ComparatorType.EMPTY && secondary.value === null) {
			return false;
		}

		return isFilterValueIncomplete(secondary.value);
	}, []);

	return (
		<DropdownWithSecondaryPicker<W_ReportType_FindAllReportBlockFieldsForFilter_VM_Single, SecondaryFilter>
			controlPrimaryOption={true}
			controlSecondaryOption={true}
			defaultValue={selected}
			filterable={true}
			initiallyOpened={initiallyOpened}
			onApplyButtonClick={onApplyFilterClick}
			onClear={removeCurrentFilter}
			onLazyLoad={lazyLoadReportBlockFieldOptions}
			onPrimaryOptionClick={onPrimaryOptionClick}
			options={reportBlockFieldOptions}
			placeholder={renderPlaceholder}
			primaryFilterName="Field"
			primaryOption={selected}
			renderPrimaryFilterItem={renderPrimaryFilterItem}
			renderSecondaryFilter={renderSecondaryFilter}
			renderSelected={renderSelected}
			secondaryFilterName="Field Value"
			secondaryOption={selectedSecondary}
			shouldDisableApplyButton={shouldDisableApplyButton}
			toggleClassName={toggleClassName}
			withCaret={withCaret}
		/>
	);
};

export default React.memo(FieldFilter);
